import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

const COLORS = {
  '$1': 'hsl(var(--muted-foreground))',
  '$2': 'hsl(210, 40%, 50%)',
  '$3': 'hsl(var(--accent))',
  '$5': 'hsl(var(--success))',
  '$10': 'hsl(var(--warning))',
  '$20+': 'hsl(350, 60%, 50%)',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground">{data.name} Tickets</p>
        <p className="text-sm text-accent">{data.count} games</p>
        <p className="text-sm text-muted-foreground">Avg ROI: {data.avgEV.toFixed(1)}%</p>
      </div>
    );
  }
  return null;
};

export const PriceTierBreakdown = ({ games }) => {
  const chartData = useMemo(() => {
    const tiers = {
      '$1': { count: 0, totalEV: 0 },
      '$2': { count: 0, totalEV: 0 },
      '$3': { count: 0, totalEV: 0 },
      '$5': { count: 0, totalEV: 0 },
      '$10': { count: 0, totalEV: 0 },
      '$20+': { count: 0, totalEV: 0 },
    };

    games.forEach(game => {
      const price = game.price;
      let tierKey;
      if (price === 1) tierKey = '$1';
      else if (price === 2) tierKey = '$2';
      else if (price === 3) tierKey = '$3';
      else if (price === 5) tierKey = '$5';
      else if (price === 10) tierKey = '$10';
      else tierKey = '$20+';

      tiers[tierKey].count++;
      tiers[tierKey].totalEV += game.ev_percent || 0;
    });

    return Object.entries(tiers)
      .filter(([_, data]) => data.count > 0)
      .map(([name, data]) => ({
        name,
        count: data.count,
        avgEV: data.count > 0 ? data.totalEV / data.count : 0,
        color: COLORS[name]
      }));
  }, [games]);

  return (
    <Card data-testid="price-tier-breakdown">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-accent"></span>
          Games by Price Tier
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[200px] w-full flex items-center">
          <ResponsiveContainer width="60%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="count"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="w-[40%] space-y-2">
            {chartData.map((tier, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: tier.color }}
                  ></span>
                  <span className="text-muted-foreground">{tier.name}</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">{tier.count}</span>
                  <span className="text-muted-foreground text-xs ml-1">
                    ({tier.avgEV.toFixed(0)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
